import React, { useState, useEffect, useCallback } from 'react';
import './style.css';

interface ImageViewerProps {
    images: string[];
    initialIndex?: number;
    isOpen: boolean;
    onClose: () => void;
    titles?: string[];
}

const ImageViewer: React.FC<ImageViewerProps> = ({
    images,
    initialIndex = 0,
    isOpen,
    onClose,
    titles
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [imageLoading, setImageLoading] = useState(true);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex, isOpen]);

    useEffect(() => {
        setImageLoading(true);
    }, [currentIndex]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentIndex]);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    }, [images.length]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    }, [images.length]);

    const goToImage = (index: number) => {
        setCurrentIndex(index);
    };

    const handleImageLoad = () => {
        setImageLoading(false);
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen || images.length === 0) return null;

    return (
        <div className="image-viewer-overlay" onClick={handleBackdropClick}>
            <div className="image-viewer-container">
                {/* Close Button */}
                <button className="viewer-close-btn" onClick={onClose}>
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Navigation Buttons */}
                {images.length > 1 && (
                    <>
                        <button className="viewer-nav-btn viewer-nav-prev" onClick={goToPrevious}>
                            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button className="viewer-nav-btn viewer-nav-next" onClick={goToNext}>
                            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}

                {/* Main Image */}
                <div className="viewer-image-wrapper">
                    {imageLoading && (
                        <div className="viewer-loading">
                            <div className="viewer-spinner"></div>
                        </div>
                    )}
                    <img 
                        key={currentIndex}
                        src={images[currentIndex]} 
                        alt={titles?.[currentIndex] || `이미지 ${currentIndex + 1}`}
                        className="viewer-image"
                        onLoad={handleImageLoad}
                        onError={handleImageLoad}
                        style={{ display: imageLoading ? 'none' : 'block' }}
                    />
                </div>

                {/* Image Info */}
                <div className="viewer-info">
                    <div className="viewer-title">
                        {titles?.[currentIndex] || `이미지 ${currentIndex + 1}`}
                    </div>
                    <div className="viewer-counter">
                        {currentIndex + 1} / {images.length}
                    </div>
                </div>

                {/* Thumbnail Strip */}
                {images.length > 1 && (
                    <div className="viewer-thumbnails">
                        <div className="thumbnails-wrapper">
                            {images.map((img, index) => (
                                <button
                                    key={index}
                                    className={`thumbnail-item ${index === currentIndex ? 'active' : ''}`}
                                    onClick={() => goToImage(index)}
                                >
                                    <img src={img} alt={`썸네일 ${index + 1}`} />
                                    {index === 0 && (
                                        <span className="thumbnail-badge">대표</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Keyboard Hints */}
                <div className="viewer-hints">
                    <span>← → 화살표로 이동</span>
                    <span>ESC 닫기</span>
                </div>
            </div>
        </div>
    );
};

export default ImageViewer;